import { useMemo } from 'react'
import ReactGridLayout from 'react-grid-layout/legacy'
import { useContainerWidth, type Layout } from 'react-grid-layout'
import { Box } from '@mui/material'
import type { LayoutItem } from '@/types/dashboard'
import 'react-grid-layout/css/styles.css'

interface DashboardGridProps {
  items: LayoutItem[]
  cols: number
  rowHeight: number
  /** Максимальная высота блока в рядах (grid.max_h из конфига API) */
  maxH: number
  editMode: boolean
  onItemsChange: (items: LayoutItem[]) => void
  /** Элемент раскладки целиком: виджету нужна и его высота в рядах */
  renderWidget: (item: LayoutItem) => React.ReactNode
}

/**
 * Сетка виджетов на react-grid-layout (v2, legacy-обёртка v1-API).
 * Drag&drop и resize активны только в режиме редактирования; перетаскивание —
 * за ручку .widget-drag-handle, чтобы контент виджетов оставался кликабельным.
 */
export function DashboardGrid({
  items,
  cols,
  rowHeight,
  maxH,
  editMode,
  onItemsChange,
  renderWidget,
}: DashboardGridProps) {
  const { width, containerRef, mounted } = useContainerWidth()

  const visible = useMemo(() => items.filter((item) => !item.hidden), [items])

  const layout: Layout = useMemo(
    () =>
      visible.map((item) => ({
        i: item.widget_key,
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h,
        minW: 1,
        maxW: cols,
        minH: 1,
        maxH,
      })),
    [visible, cols, maxH],
  )

  const handleLayoutChange = (next: Layout) => {
    // Вне editMode RGL может слать служебные onLayoutChange — игнорируем
    if (!editMode) return

    const byKey = new Map(next.map((cell) => [cell.i, cell]))
    onItemsChange(
      items.map((item) => {
        const cell = byKey.get(item.widget_key)
        if (!cell || item.hidden) return item
        return { ...item, x: cell.x, y: cell.y, w: cell.w, h: cell.h }
      }),
    )
  }

  return (
    <Box ref={containerRef} sx={{ width: '100%' }}>
      {mounted && width > 0 && (
        <ReactGridLayout
          width={width}
          cols={cols}
          rowHeight={rowHeight}
          margin={[16, 16]}
          containerPadding={[0, 0]}
          layout={layout}
          isDraggable={editMode}
          isResizable={editMode}
          draggableHandle=".widget-drag-handle"
          onLayoutChange={handleLayoutChange}
        >
          {visible.map((item) => (
            <div key={item.widget_key}>{renderWidget(item)}</div>
          ))}
        </ReactGridLayout>
      )}
    </Box>
  )
}
